
import React, { useState, useEffect, useRef } from 'react';
import { Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { API_BASE_URL } from '../config';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Upload, Video, Link, Copy, Trash2, RefreshCw, Clock, CheckCircle, AlertTriangle, Film, ExternalLink } from 'lucide-react';

interface VideoItem {
    id: number; user_id: string; username: string; original_name: string;
    file_size_mb: number; description: string; share_token: string;
    watch_url: string; direct_url: string; created_at: string;
    expires_at: string; days_left: number;
}

export const VideoUpload: React.FC = () => {
    const { user } = useAuth();
    const canSeeAll = user?.roles?.some((r: string) => [UserRole.ADMIN, UserRole.WEBADMIN, 'STEWARD'].includes(r as any)) || false;
    const [videos, setVideos] = useState<VideoItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [description, setDescription] = useState('');
    const [copied, setCopied] = useState('');
    const [error, setError] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);

    const loadVideos = () => {
        setLoading(true);
        const url = canSeeAll ? 'video_upload.php' : `video_upload.php?userId=${user?.id || ''}`;
        apiRequest<any>(url).then(r => {
            setVideos(Array.isArray(r) ? r : []);
        }).catch(() => setVideos([])).finally(() => setLoading(false));
    };

    useEffect(() => { loadVideos(); }, []);

    const handleUpload = async (file: File) => {
        if (!user) return;
        if (file.size > 100 * 1024 * 1024) { setError('Max 100MB.'); return; }
        
        setUploading(true); setError(''); setProgress(0);
        const formData = new FormData();
        formData.append('video', file);
        formData.append('userId', user.id);
        formData.append('username', user.username);
        formData.append('description', description);

        try {
            const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
            const url = `${baseUrl}/video_upload.php`;
            
            const xhr = new XMLHttpRequest();
            await new Promise<void>((resolve, reject) => {
                xhr.upload.onprogress = (e) => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        const res = JSON.parse(xhr.responseText);
                        if (res.status === 'error') reject(new Error(res.message));
                        else resolve();
                    } else reject(new Error('Upload fehlgeschlagen'));
                };
                xhr.onerror = () => reject(new Error('Netzwerkfehler'));
                xhr.open('POST', url);
                xhr.send(formData);
            });
            
            setDescription('');
            loadVideos();
        } catch (e: any) { setError(e.message); }
        finally { setUploading(false); setProgress(0); }
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Video löschen?')) return;
        try {
            await apiRequest('video_upload.php', 'DELETE', { id });
            loadVideos();
        } catch (e: any) { setError(e.message); }
    };

    const copyLink = (url: string, id: string) => {
        navigator.clipboard.writeText(url);
        setCopied(id);
        setTimeout(() => setCopied(''), 2000);
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-20">
            {/* Header */}
            <div className="relative overflow-hidden rounded-xl border border-gray-800/40 bg-gradient-to-br from-[#12121a] via-[#15151e] to-[#12121a] p-8">
                <div className="absolute top-0 right-0 w-48 h-48 bg-red-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
                <div className="flex items-center gap-3">
                    <div className="w-1.5 h-10 bg-f1-red rounded-full" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-white leading-none">Video Upload</h1>
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.3em] mt-1">Clip hochladen · Link teilen · 7 Tage gültig</p>
                    </div>
                </div>
            </div>

            {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2"><AlertTriangle size={16}/>{error}</div>}

            {/* Upload Area */}
            <div className="rounded-xl border border-gray-800/40 bg-[#12121a] p-6 space-y-4">
                <div className="text-[10px] text-gray-600 font-bold uppercase tracking-wider">Neues Video</div>
                <input
                    type="text" placeholder="Beschreibung (optional, z.B. 'Runde 5 Vorfall T3')"
                    className="w-full bg-[#0e0e16] border border-gray-800/40 rounded-lg px-4 py-2 text-sm text-white outline-none focus:border-f1-red"
                    value={description} onChange={e => setDescription(e.target.value)}
                />
                <div
                    className={`rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer ${uploading ? 'border-f1-red/30 bg-f1-red/5' : 'border-gray-800/40 hover:border-f1-red/30 hover:bg-f1-red/5'}`}
                    onClick={() => !uploading && fileRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={e => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}
                >
                    <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" className="hidden" onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); }} />
                    {uploading ? (
                        <div>
                            <RefreshCw size={32} className="mx-auto mb-2 text-f1-red animate-spin"/>
                            <p className="text-white font-bold text-sm">Uploading... {progress}%</p>
                            <div className="w-48 h-1.5 bg-gray-800 rounded-full mx-auto mt-3 overflow-hidden">
                                <div className="h-full bg-f1-red rounded-full transition-all" style={{width: `${progress}%`}}/>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <Upload size={32} className="mx-auto mb-2 text-gray-600"/>
                            <p className="text-white font-bold text-sm">Video hochladen oder hierher ziehen</p>
                            <p className="text-gray-600 text-[10px] mt-1">MP4, WebM, MOV · Max 100MB · 7 Tage gültig</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Video List */}
            <div className="rounded-xl border border-gray-800/40 bg-[#12121a] overflow-hidden">
                <div className="h-[2px] bg-gradient-to-r from-f1-red via-f1-red/50 to-transparent"/>
                <div className="px-5 py-3 border-b border-gray-800/30 flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-[0.15em] text-white/90">Hochgeladene Videos ({videos.length})</span>
                    <button onClick={loadVideos} className="text-gray-600 hover:text-white"><RefreshCw size={14}/></button>
                </div>

                {loading ? (
                    <div className="p-10 text-center"><RefreshCw className="animate-spin mx-auto text-gray-600"/></div>
                ) : videos.length === 0 ? (
                    <div className="p-10 text-center text-gray-600 text-sm">Noch keine Videos.</div>
                ) : (
                    <div className="divide-y divide-gray-800/20">
                        {videos.map(v => (
                            <div key={v.id} className="px-5 py-4 hover:bg-white/[0.02] transition-colors">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center shrink-0">
                                        <Film size={18} className="text-gray-600"/>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-bold text-white truncate">{v.original_name}</span>
                                            <Badge className="bg-gray-800 text-gray-500 text-[8px]">{v.file_size_mb}MB</Badge>
                                        </div>
                                        {v.description && <p className="text-[10px] text-gray-500 mb-1">{v.description}</p>}
                                        <div className="flex items-center gap-3 text-[9px] text-gray-600">
                                            <span>{v.username}</span>
                                            <span>·</span>
                                            <span className="flex items-center gap-1"><Clock size={9}/>{v.days_left}d übrig</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <a href={v.watch_url} target="_blank" rel="noreferrer" className="p-2 rounded bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" title="Abspielen">
                                            <ExternalLink size={14}/>
                                        </a>
                                        <button onClick={() => copyLink(v.watch_url, v.share_token)} className={`p-2 rounded transition-colors ${copied === v.share_token ? 'bg-green-800 text-green-400' : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'}`} title="Link kopieren">
                                            {copied === v.share_token ? <CheckCircle size={14}/> : <Copy size={14}/>}
                                        </button>
                                        {(user?.id === v.user_id || user?.roles?.includes(UserRole.ADMIN) || user?.roles?.includes(UserRole.WEBADMIN)) && (
                                            <button onClick={() => handleDelete(v.id)} className="p-2 rounded bg-gray-800 text-gray-400 hover:text-red-500 hover:bg-red-900/20 transition-colors" title="Löschen">
                                                <Trash2 size={14}/>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
